import { ApiError, SiteChoiceRequiredError } from "../errors.js";
import { ClientBase } from "./base.js";
export class AuthClient extends ClientBase {
    async register(input) {
        return this.request("POST", "/v1/auth/register", input);
    }
    /** Creates a new tenant site + owner account and stores the returned tokens. */
    async signupSite(input) {
        const res = await this.request("POST", "/v1/auth/signup-site", input);
        this.setTokens({ access: res.access, refresh: res.refresh });
        return res;
    }
    /**
     * Password login. Returns the session on the normal path. If the backend answers with a
     * site-choice (empty site, password valid for several tenants) this throws
     * {@link SiteChoiceRequiredError}; use {@link loginWithSiteChoice} to handle that step.
     */
    async login(input) {
        const res = await this.loginWithSiteChoice(input);
        if ("requiresSiteChoice" in res)
            throw new SiteChoiceRequiredError(res.sites);
        return res;
    }
    /** Like {@link login} but returns the site-choice response instead of throwing; stores tokens only for a session. */
    async loginWithSiteChoice(input) {
        const body = {
            email: input.email,
            password: input.password,
        };
        if (input.site)
            body.site = input.site;
        const res = await this.request("POST", "/v1/auth/login", body);
        if ("requiresSiteChoice" in res && res.requiresSiteChoice) {
            return { requiresSiteChoice: true, sites: res.sites ?? [] };
        }
        const session = res;
        this.setTokens({ access: session.access, refresh: session.refresh });
        return session;
    }
    /**
     * Replaces the signed-in user's own password (#1077). Works while a temporary credential
     * is in force; stores and returns the fresh tokens, which no longer carry the forced-change flag.
     */
    async changePassword(input) {
        const pair = await this.request("POST", "/v1/auth/change-password", input, true);
        this.setTokens({ access: pair.access, refresh: pair.refresh });
        return pair;
    }
    /** Owner request/event log (GET /v1/logs, logs:read), newest first, cursor via `before`/`next`. */
    async getTenantLogs(params = {}) {
        const q = new URLSearchParams();
        if (params.limit !== undefined)
            q.set("limit", String(params.limit));
        if (params.before)
            q.set("before", params.before);
        if (params.kind)
            q.set("kind", params.kind);
        return this.request("GET", `/v1/logs?${q.toString()}`, undefined, true);
    }
    /** Consumes a one-time owner invite and stores the returned JWT pair. */
    async acceptOwnerInvite(input) {
        const pair = await this.request("POST", "/v1/auth/accept-owner-invite", input);
        this.setTokens(pair);
        return pair;
    }
    /** Exchanges a refresh token for a new access/refresh pair (plus the site user, #1184) and stores the pair. */
    async refresh(refreshToken) {
        const refresh = refreshToken ?? (this.getTokens?.() ?? this.tokens)?.refresh;
        if (!refresh) {
            throw new ApiError(401, "unauthorized", "missing refresh token");
        }
        const pair = await this.request("POST", "/v1/auth/refresh", { refresh });
        this.setTokens({ access: pair.access, refresh: pair.refresh });
        return pair;
    }
    /**
     * Drops local tokens and best-effort ends the server-side session (#463, #1193): the
     * session's access tokens stop working everywhere. `{ all: true }` ends every session
     * of this user on the site.
     */
    logout(options = {}) {
        const tokens = this.getTokens?.() ?? this.tokens;
        this.setTokens(null);
        if (!tokens?.refresh && !tokens?.access)
            return;
        const headers = {};
        if (tokens.access)
            headers["Authorization"] = `Bearer ${tokens.access}`;
        const path = options.all ? "/v1/auth/logout?all=true" : "/v1/auth/logout";
        const body = tokens.refresh ? { refresh: tokens.refresh } : {};
        this.request("POST", path, body, false, false, headers).catch(() => undefined);
    }
}
//# sourceMappingURL=auth.js.map